import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Upload as UploadIcon, CheckCircle, Error, Close as CloseIcon } from "@mui/icons-material";
import { BlockBlobClient } from "@azure/storage-blob";
import { requestUploadSas, completeUpload, fetchUploadDatasets } from "../../../services/RiskMonitorAxios";
import { dateFromFilename } from "./uploadFilenameDate";

const BLOCK_SIZE = 4 * 1024 * 1024;

function RiskMonitorUpload({ open, onClose }) {
  const [datasetOptions, setDatasetOptions] = useState([]);
  const [dataset, setDataset] = useState("simulation_pnl");
  // Fallback for a file whose name has no date. A detected filename date
  // wins; each row can still be edited before upload.
  const [defaultDate, setDefaultDate] = useState("");
  const [files, setFiles] = useState([]);
  const [fileDates, setFileDates] = useState([]);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dataset options reflect whatever folders actually exist in storage
  // (see storage_paths.list_datasets) — a new folder created directly in
  // storage shows up here without a code change.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchUploadDatasets()
      .then((res) => {
        if (cancelled) return;
        const options = res?.datasets || [];
        setDatasetOptions(options);
        setDataset((current) => (options.includes(current) ? current : options[0] || ""));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load dataset list:", err?.response?.status, err?.response?.data || err.message);
        setError(
          err?.response?.status === 403
            ? "Your account doesn't have the role.risk_upload permission needed to list datasets — check with an admin."
            : "Failed to load dataset list. Check the console for details.",
        );
      });
    return () => { cancelled = true; };
  }, [open]);

  const resetForm = () => {
    setDataset("simulation_pnl");
    setDefaultDate("");
    setFiles([]);
    setFileDates([]);
    setUploadStatus([]);
    setError(null);
    setSuccess(null);
    setIsDragging(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose?.();
  };

  const [isDragging, setIsDragging] = useState(false);

  const applyFiles = (selectedFiles) => {
    const parquetFiles = selectedFiles.filter((f) => f.name.toLowerCase().endsWith(".parquet"));

    if (selectedFiles.length === 0) {
      setError("No files selected");
      return;
    }

    if (parquetFiles.length !== selectedFiles.length) {
      setError("Only .parquet files are allowed");
      return;
    }

    if (parquetFiles.length > 20) {
      setError("Maximum 20 files per upload");
      return;
    }

    setFiles(parquetFiles);
    setFileDates(parquetFiles.map((file) => dateFromFilename(file.name) || defaultDate));
    setError(null);
    setSuccess(null);
    setUploadStatus(parquetFiles.map((f) => ({ name: f.name, progress: 0, status: "pending" })));
  };

  const handleFileDateChange = (index, value) => {
    setFileDates((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleFileChange = (event) => {
    applyFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (uploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (uploading) return;
    applyFiles(Array.from(event.dataTransfer.files || []));
  };

  const uploadFileToBlob = async (file, uploadUrl, index) => {
    const blobClient = new BlockBlobClient(uploadUrl);

    try {
      await blobClient.uploadData(file, {
        blockSize: BLOCK_SIZE,
        concurrency: 5,
        onProgress: (progress) => {
          const percent = Math.round((progress.loadedBytes / file.size) * 100);
          setUploadStatus((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], progress: percent, status: "uploading" };
            return updated;
          });
        },
      });

      setUploadStatus((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], progress: 100, status: "complete" };
        return updated;
      });

      return { success: true, size: file.size };
    } catch (err) {
      setUploadStatus((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status: "error", error: err.message };
        return updated;
      });
      return { success: false, error: err.message };
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select files to upload");
      return;
    }

    if (fileDates.some((d) => !d)) {
      setError("Please set a valuation date for every file");
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const sasResponse = await requestUploadSas({
        dataset,
        files: files.map((f, index) => ({
          filename: f.name,
          valuation_date: fileDates[index],
        })),
      });

      const uploadPromises = sasResponse.uploads.map((upload, index) => {
        const file = files[index];
        if (!file) {
          return Promise.resolve({ success: false, error: `No file matched for ${upload.blob_path}` });
        }
        return uploadFileToBlob(file, upload.upload_url, index);
      });

      const results = await Promise.all(uploadPromises);

      const blobs = sasResponse.uploads.map((upload, index) => ({
        blob_path: upload.blob_path,
        size_bytes: results[index].success ? results[index].size : 0,
      }));

      const failedUploads = results.filter((r) => !r.success);
      if (failedUploads.length > 0) {
        const firstError = failedUploads[0].error || "unknown error";
        const corsBlocked = /Failed to fetch|CORS|NetworkError/i.test(firstError);
        setError(
          corsBlocked
            ? `Direct upload to storage was blocked by CORS. Guest-pass URL was issued, but the browser could not PUT to Azure. In Azure Portal → storage account → Resource sharing (CORS) → Blob service, add this exact origin (${window.location.origin}), methods GET/PUT/POST/HEAD/OPTIONS, headers *.`
            : `Upload to storage failed: ${firstError}`,
        );
        return;
      }

      const verifyResponse = await completeUpload({
        dataset,
        blobs,
      });

      const allVerified = verifyResponse.verified.every((v) => v.exists && v.size_match);
      const dateCount = new Set(fileDates).size;

      if (allVerified) {
        setSuccess(
          `Successfully uploaded ${files.length} file(s) across ${dateCount} date(s) to glazer_risk/raw`,
        );
      } else if (verifyResponse.status === "partial") {
        setError("Some files uploaded but were not fully verified. Check the status below.");
      } else {
        const missing = verifyResponse.verified.filter((v) => !v.exists).map((v) => v.blob_path);
        setError(
          `Upload finished but the API could not find the blob(s): ${missing.join(", ") || "unknown path"}`,
        );
      }
    } catch (err) {
      setError(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <UploadIcon sx={{ color: "#006842" }} />
        Upload G-VaR Data
        <IconButton
          onClick={handleClose}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload Parquet files to `glazer_risk/raw`. They become available to G-VaR after upload.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Dataset</InputLabel>
          <Select value={dataset} onChange={(e) => setDataset(e.target.value)} label="Dataset" disabled={uploading}>
            {datasetOptions.map((ds) => (
              <MenuItem key={ds} value={ds}>
                {ds}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          type="date"
          label="Default Valuation Date"
          value={defaultDate}
          onChange={(e) => setDefaultDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Used only when a filename has no date. A date in the name, such as 20260803, fills that file's row."
          sx={{ mb: 2 }}
          disabled={uploading}
        />

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            mb: 2,
            px: 2,
            py: 3,
            border: "2px dashed",
            borderColor: isDragging ? "#006842" : "rgba(0,104,66,0.35)",
            bgcolor: isDragging ? "rgba(0,104,66,0.06)" : "transparent",
            borderRadius: 2,
            textAlign: "center",
            cursor: uploading ? "not-allowed" : "pointer",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
          }}
        >
          <UploadIcon sx={{ color: "#006842", mb: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#006842" }}>
            {isDragging ? "Drop Parquet files here" : "Drag and drop Parquet files here"}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            or
          </Typography>
          <Button variant="outlined" component="label" disabled={uploading} size="small">
            Browse files
            <input type="file" hidden multiple accept=".parquet" onChange={handleFileChange} />
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            .parquet only, max 20 files
          </Typography>
        </Box>

        {files.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files ({files.length})
            </Typography>
            <List dense>
              {uploadStatus.map((status, index) => (
                <ListItem key={index} sx={{ px: 0, gap: 1, alignItems: "center" }}>
                  <ListItemText
                    primary={status.name}
                    secondary={
                      status.status === "uploading"
                        ? `${status.progress}%`
                        : status.status === "complete"
                          ? "Complete"
                          : status.status === "error"
                            ? `Error: ${status.error}`
                            : "Pending"
                    }
                  />
                  <TextField
                    type="date"
                    size="small"
                    label={fileDates[index] ? "Valuation date" : "Enter date"}
                    value={fileDates[index] || ""}
                    onChange={(e) => handleFileDateChange(index, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!fileDates[index]}
                    helperText={fileDates[index] ? " " : "No date in the filename"}
                    disabled={uploading}
                    sx={{ width: 168 }}
                  />
                  {status.status === "complete" && <CheckCircle color="success" />}
                  {status.status === "error" && <Error color="error" />}
                  {status.status === "uploading" && <Chip label={`${status.progress}%`} size="small" />}
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {uploading && <LinearProgress sx={{ mb: 2 }} />}

        <Alert severity="info">
          <Typography variant="caption">
            Files are written to `glazer_risk/raw` and are used by G-VaR immediately.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || files.length === 0 || fileDates.some((d) => !d)}
          startIcon={<UploadIcon />}
          sx={{ bgcolor: "#006842", "&:hover": { bgcolor: "#005233" } }}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RiskMonitorUpload;
